import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { SvgIconComponent } from "angular-svg-icon";
import { HighchartsChartModule } from "highcharts-angular";
import { map, startWith } from "rxjs";

import { ChangeDetectionStrategy, Component, computed, inject, input, resource, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

import { PacketType } from "../../../apis/stats/rest/models/packet-type";
import { TrafficService } from "../../../apis/stats/rest/services";
import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { reportAndReturn } from "../../../helpers/report-to-sentry";
import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
} from "../../common/line-chart/components/date-picker/date-picker.component";
import { LineChartRadioButtons, RadioOption } from "../../common/line-chart/line-chart-radio-buttons";
import { LineChartSubjectDropdown, SubjectOrGroup } from "../../common/line-chart/line-chart-subject-dropdown";
import { LineChart } from "../../common/line-chart/line-chart.component";
import { getDateRange, toLineChartLine } from "../../common/line-chart/line-chart.helpers";
import {
  EMPTY_TRAFFIC,
  LINE_METADATA,
  PACKET_TYPE_OPTIONS,
  TrafficSubject,
  makeBpsCustomization,
  makePpsCustomization,
} from "./traffic-line-chart.helpers";

@Component({
  selector: "traffic-line-chart",
  imports: [
    HighchartsChartModule,
    LineChart,
    LineChartRadioButtons,
    LineChartSubjectDropdown,
    ReadableSizePipe,
    SvgIconComponent,
    TranslatePipe,
  ],
  templateUrl: "./traffic-line-chart.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./traffic-line-chart.component.scss",
})
export class TrafficLineChart {
  public readonly subjects = input.required<SubjectOrGroup<TrafficSubject>[]>();
  private readonly source = inject(TrafficService);
  private readonly translate = inject(TranslateService);

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      map((e) => e.lang),
      startWith(this.translate.getCurrentLang()),
    ),
    { requireSync: true },
  );

  protected readonly trafficMetadata = computed(() => {
    this.currentLang(); // re-evaluate the labels on language change
    return Object.fromEntries(
      Object.entries(LINE_METADATA).map(([k, v]) => [
        k,
        { color: v.color, label: this.translate.instant(v.label) as string },
      ]),
    );
  });
  protected readonly mode = signal<"bps" | "pps">("bps");
  protected readonly modeOptions: RadioOption<"bps" | "pps">[] = [
    { value: "bps", label: "Chart.Label.BitsS" },
    { value: "pps", label: "Chart.Label.PacketsS" },
  ];
  protected readonly customization = computed(() =>
    this.mode() === "bps" ? makeBpsCustomization(this.translate) : makePpsCustomization(this.translate),
  );
  protected readonly packetType = signal<PacketType>(PacketType.Unicast);
  protected readonly packetTypeOptions = computed(() =>
    PACKET_TYPE_OPTIONS.map((o) => ({ ...o, select: o.type === this.packetType() })),
  );

  // TODO Implement streaming the *real* live data
  protected readonly latestIn = computed(() => Math.abs(this.traffic.value().in.at(-1)?.[1] ?? 0));

  // TODO Implement streaming the *real* live data
  protected readonly latestOut = computed(() => this.traffic.value().out.at(-1)?.[1] ?? 0);

  protected readonly selectedSubject = signal<TrafficSubject | undefined>(undefined);
  protected readonly dateRangeOfInterest = signal<[Date, Date]>([DEFAULT_START_DATE, DEFAULT_END_DATE]);

  protected readonly traffic = resource({
    params: () => {
      const subject = this.selectedSubject();
      if (!subject) return undefined;
      return {
        subject,
        dateRange: this.dateRangeOfInterest(),
        mode: this.mode(),
        packetType: this.packetType(),
      };
    },
    loader: async ({ params }) => {
      const queryParams = paramsToQueryParams(params.subject, params.dateRange);
      const request =
        params.mode === "bps" ?
          this.source.getServiceTrafficBps(queryParams)
        : this.source.getServiceTrafficPackets({ ...queryParams, packet_type: params.packetType });
      const raw = await request.catch(reportAndReturn(undefined));
      if (!raw) return EMPTY_TRAFFIC;
      return {
        // Peaks first: Highcharts draws series in insertion order, so in/out must be added last to render on top.
        // Negate invalues as we want to draw incoming traffic downwards
        // TODO: See if API can be changed to return [number, number] instead of [number]
        peakIn: toLineChartLine(raw.peakIn ?? [], -1),
        peakOut: toLineChartLine(raw.peakOut ?? []),
        in: toLineChartLine(raw.in ?? [], -1),
        out: toLineChartLine(raw.out ?? []),
      };
    },
    defaultValue: EMPTY_TRAFFIC,
  });

  protected updatePacketType(selected: number[]) {
    const option = PACKET_TYPE_OPTIONS[selected[0]];
    if (option) this.packetType.set(option.type);
  }
}

function paramsToQueryParams(subject: TrafficSubject, dateRange: [Date, Date]) {
  const { start, end, step } = getDateRange(dateRange);
  const subscription_id = subject.subscriptionId;
  const subscription_instance_ids = subject.subscriptionInstanceIds;
  return { subscription_id, subscription_instance_ids, start, end, step };
}
